import { ArrowRight, Plane } from 'lucide-react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { RevealableName } from '@/components/pnr/revealable-name';
import { PassengerConditionControl } from '@/components/pnr/passenger-condition-control';
import type { DisruptionPaxRosterRow } from '@/lib/disruptions/types';
import { priorityShortLabel } from '@/routes/disruptions/labels';
import { formatFlightDateTime } from '@/lib/disruptions/airport-tz';
import type { INotificationStatusRow } from '@/lib/notifications/types';
import { ACCEPTANCE_TONE, DELIVERY_TONE } from '@/lib/notifications/labels';

// One passenger row inside the PNR's Passengers card. Borderless — the card draws
// a horizontal divider between rows (no per-row card box, which looked like a
// double line). The rebooking-status badge + option count are intentionally NOT
// shown here: they're redundant with the hero status chip + the left-column
// options. The URN is dropped too (not operator-facing). Kept: the audited name
// reveal, pax type / loyalty / senior, the operational status (when acted on),
// priority tags, the flight + onward leg, and any assistance needs.

/** Translation key for an operator-set operational passenger status — this file
 *  holds keys, never language. Unmapped codes fall back to the raw code. */
const PAX_STATUS_KEY: Record<string, string> = {
  CHECKED_IN: 'paxStatus.checkedIn',
  CHECKED_OUT: 'paxStatus.checkedOut',
  NO_CONTINGENCY: 'paxStatus.noContingency',
  TRANSFER_REQUESTED: 'paxStatus.transferRequested',
};

/** Badge tone per accommodation condition (#806) — UNKNOWN is the one that blocks. */
const CONDITION_TONE: Record<string, 'neutral' | 'info' | 'warning' | 'error'> = {
  ADULT: 'neutral',
  ACCOMPANIED_MINOR: 'info',
  UNACCOMPANIED_MINOR: 'warning',
  UNKNOWN: 'error',
};

export function PnrPaxRow({
  row,
  caseUrn,
  disruptionUrn,
  notification,
}: {
  row: DisruptionPaxRosterRow;
  caseUrn: string;
  /** When known, the row offers the desk's condition / responsible-adult writes (#806). */
  disruptionUrn?: string | undefined;
  /** The PNR group's notification + acceptance status (N11), when loaded. Every pax
   *  in a PNR shares the group's offer, so the chip reflects the sub-case gate. */
  notification?: INotificationStatusRow | undefined;
}) {
  const { t } = useTranslation('pnr');
  const statusKey = row.status ? PAX_STATUS_KEY[row.status] : undefined;
  return (
    <div className="py-3 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <RevealableName caseUrn={caseUrn} paxUrn={row.paxUrn} masked={row.fullName} className="font-medium" />
          <Badge variant="muted" size="sm">
            {row.paxType}
          </Badge>
          {row.loyaltyTier ? (
            <Badge variant="info" size="sm">
              {row.loyaltyTier}
            </Badge>
          ) : null}
          {row.isSenior ? (
            <Badge variant="neutral" size="sm">
              {t('pax.senior')}
            </Badge>
          ) : null}
          {/* Accommodation condition (#806): adult / accompanied minor / unaccompanied
              minor — or UNKNOWN, which holds the whole party back until the desk fixes it. */}
          {row.condition ? (
            <Badge
              variant={CONDITION_TONE[row.condition] ?? 'neutral'}
              size="sm"
              title={conditionTitle(row, t)}
              data-testid="pax-condition"
            >
              {t(`condition.value.${row.condition}`)}
            </Badge>
          ) : null}
          {/* passenger-profile-completion: this entry was completed by the passenger
              (counter QR) or corrected at the desk; the document KIND they declared. */}
          {row.profileSource ? (
            <Badge variant={row.profileSource === 'PASSENGER_SELF_SERVICE' ? 'success' : 'neutral'} size="sm" data-testid="pax-profile-source">
              {t(`profile.source.${row.profileSource}`)}
            </Badge>
          ) : null}
          {row.documentType ? (
            <Badge variant="muted" size="sm" data-testid="pax-document-type">
              {t(`profile.documentType.${row.documentType}`)}
            </Badge>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          {/* Notification + acceptance status column (N11). The acceptance chip is the
              gate signal the operator works; delivery is secondary telemetry. Hidden
              pre-gate (`none`) so an un-offered PNR isn't noise. */}
          {notification && notification.acceptanceState !== 'none' ? (
            <Badge
              variant={ACCEPTANCE_TONE[notification.acceptanceState]}
              size="sm"
              title={t('pax.acceptanceTitle', {
                state: t(`acceptance.state.${notification.acceptanceState}`),
              })}
              data-testid="pax-acceptance-status"
            >
              {t(`acceptance.state.${notification.acceptanceState}`)}
            </Badge>
          ) : null}
          {notification && notification.deliveryState !== 'unknown' ? (
            <Badge
              variant={DELIVERY_TONE[notification.deliveryState]}
              size="sm"
              title={t('pax.deliveryTitle', {
                state: t(`delivery.state.${notification.deliveryState}`),
              })}
              data-testid="pax-delivery-status"
            >
              {t(`delivery.state.${notification.deliveryState}`)}
            </Badge>
          ) : null}
          {/* Operational status — only when the operator has acted on the passenger. */}
          {row.status && row.status !== 'AWAITING' && row.status !== 'AWAITING_OPERATOR' ? (
            <Badge
              variant="neutral"
              size="sm"
              title={t('pax.operationalTitle', { status: row.status })}
            >
              {statusKey ? t(statusKey) : row.status}
            </Badge>
          ) : null}
        </div>
      </div>

      {row.priorityTags.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-1">
          {row.priorityTags.map((tag) => (
            <Badge key={tag} variant="warning" size="sm">
              {priorityShortLabel(tag, t)}
            </Badge>
          ))}
        </div>
      ) : null}

      {disruptionUrn ? (
        <PassengerConditionControl row={row} caseUrn={caseUrn} disruptionUrn={disruptionUrn} />
      ) : null}

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <span className="inline-flex items-center gap-1">
          <Plane className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
          <span className="font-medium">{row.flightLabel}</span>
        </span>
        <span className="text-muted-foreground">{formatFlightDateTime(row.departureAt, row.origin)}</span>
        {row.onwardFlightLabel ? (
          <span className="inline-flex items-center gap-1 text-muted-foreground">
            <ArrowRight className="h-3.5 w-3.5" aria-hidden />
            <span className="font-medium text-foreground">{row.onwardFlightLabel}</span>
            {row.onwardDepartureAt ? (
              <span>{formatFlightDateTime(row.onwardDepartureAt, row.onwardOrigin)}</span>
            ) : null}
          </span>
        ) : null}
      </div>

      {row.specialNeeds && row.specialNeeds.length > 0 ? (
        <p className="mt-1.5 text-xs text-muted-foreground">
          {t('pax.needs', { list: row.specialNeeds.join(', ') })}
        </p>
      ) : null}
    </div>
  );
}

/** Tooltip for the condition badge: why it holds its value, and who set it when. */
function conditionTitle(row: DisruptionPaxRosterRow, t: TFunction<'pnr'>): string {
  const parts: string[] = [];
  if (row.condition === 'UNKNOWN') {
    const basis = row.conditionBasis ?? 'NONE';
    parts.push(t(`condition.unknownHint.${basis}`, { defaultValue: t('condition.unknownHint.NONE') }));
  } else if (row.conditionBasis) {
    parts.push(t(`condition.basis.${row.conditionBasis}`, { defaultValue: row.conditionBasis }));
  }
  if (row.conditionSource === 'OPERATOR' && row.conditionSetAt) {
    parts.push(
      t('condition.setBy', {
        // #813: the display name travels on the stamp; the URN is the fallback.
        who: row.conditionSetByName ?? row.conditionSetBy ?? t('condition.anOperator'),
        time: new Date(row.conditionSetAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      }),
    );
  }
  if (row.responsibleAdult) {
    parts.push(t('condition.responsibleAdult', { pnr: row.responsibleAdult.pnrLocator }));
  }
  return parts.join(' · ');
}
